import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO_WHITE = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";

// Desktop/tablet is allowed on these (marketing, auth, IBRA admin, public flyer).
const DESKTOP_OK = ["/", "/login", "/register"];
const isDesktopOkPath = (p) => DESKTOP_OK.includes(p) || p.startsWith("/admin") || p.startsWith("/flyer") || p.startsWith("/verify") || p.startsWith("/forgot-password") || p.startsWith("/reset-password") || p.startsWith("/messaging-policy") || p.startsWith("/privacy") || p.startsWith("/terms") || p.startsWith("/publication") || p.startsWith("/claim");

function detectPhone() {
  if (typeof window === "undefined") return true;
  const ua = /Mobi|Android|iPhone|iPod/i.test(navigator.userAgent || "");
  const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const minSide = Math.min(window.screen?.width || 9999, window.screen?.height || 9999);
  // Phones have a coarse pointer AND a small physical dimension (<=500). Tablets are excluded.
  return ua || (coarse && minSide <= 500);
}

export default function MobileGate() {
  const { user } = useAuth();
  const loc = useLocation();
  const [isPhone, setIsPhone] = useState(detectPhone);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onResize = () => setIsPhone(detectPhone());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => { setDismissed(false); }, [loc.pathname]);

  const isAdmin = user && user.role === "admin";
  if (isPhone || isAdmin || dismissed || isDesktopOkPath(loc.pathname)) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: "rgba(11,11,12,0.97)", backdropFilter: "blur(6px)" }}
      data-testid="mobile-gate">
      <div className="w-full max-w-md text-center text-white">
        <img src={LOGO_WHITE} alt="Text Racing" className="h-9 w-auto mx-auto mb-8" />
        <div className="flex items-center justify-center gap-2 mb-4 text-race-sub">
          <Smartphone size={18} />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Phone Sport</span>
        </div>
        <h1 className="font-heading text-5xl sm:text-6xl leading-[0.9] mb-4" style={{ textWrap: "balance" }}>
          Pit Lane is<br />mobile‑only
        </h1>
        <p className="text-race-sub text-sm max-w-sm mx-auto mb-8">
          Text Racing is played on the custom keyboard, which only runs on a phone. Scan the code to continue on your device.
        </p>
        <div className="inline-block p-4 bg-white rounded-md">
          <QRCodeSVG value={url} size={172} fgColor="#0B0B0C" bgColor="#FFFFFF" data-testid="mobile-gate-qr" />
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-race-muted break-all">{url}</p>
        <button onClick={() => setDismissed(true)} data-testid="mobile-gate-continue"
          className="mt-8 text-xs font-mono uppercase tracking-widest text-race-muted hover:text-white transition-colors underline underline-offset-4">
          Continue on desktop anyway
        </button>
      </div>
    </div>
  );
}
